import { useMemo } from 'react'
import { cn } from '@/lib/utils/cn'

export type DatePreset = 'mtd' | 'qtd' | 'ytd' | '12mo' | 'custom'

export interface DateRange {
  from: string // ISO date
  to: string // ISO date
}

export interface DateRangePickerProps {
  value?: DateRange
  onChange?: (range: DateRange, preset: DatePreset) => void
  /** Preset shown as active. If omitted, computed from value. */
  preset?: DatePreset
  /** Today override for testing. */
  today?: Date
  className?: string
  /**
   * 2026-07-21 — ISO date floor. When set, both "From"/"To" inputs get a
   * native `min` attribute, and preset-computed start dates (mtd/qtd/ytd/
   * 12mo) are clamped up to this date if they'd otherwise land earlier.
   * Optional and additive — every other consumer of this shared primitive
   * is unaffected when omitted.
   */
  minDate?: string
}

const PRESETS: { key: Exclude<DatePreset, 'custom'>; label: string }[] = [
  { key: 'mtd', label: 'MTD' },
  { key: 'qtd', label: 'QTD' },
  { key: 'ytd', label: 'YTD' },
  { key: '12mo', label: '12 mo' },
]

function computeRange(preset: Exclude<DatePreset, 'custom'>, today: Date): DateRange {
  const end = today
  let start: Date
  switch (preset) {
    case 'mtd':
      start = new Date(end.getFullYear(), end.getMonth(), 1)
      break
    case 'qtd': {
      const q = Math.floor(end.getMonth() / 3)
      start = new Date(end.getFullYear(), q * 3, 1)
      break
    }
    case 'ytd':
      start = new Date(end.getFullYear(), 0, 1)
      break
    case '12mo':
      start = new Date(end.getFullYear() - 1, end.getMonth(), end.getDate())
      break
  }
  return { from: toIso(start), to: toIso(end) }
}

function toIso(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/**
 * Preset-first date range (MTD / QTD / YTD / 12mo) with manual overrides.
 * No calendar popover in M2 — that comes once Radix Popover lands in M4
 * and we layer a proper month grid on top. Day-1 reporting rarely needs
 * arbitrary ranges anyway (spec §DataView filter rail).
 */
export function DateRangePicker({
  value,
  onChange,
  preset = 'custom',
  today = new Date(),
  className,
  minDate,
}: DateRangePickerProps) {
  const computedDefault = useMemo(() => computeRange('ytd', today), [today])
  const range = value ?? computedDefault

  const selectPreset = (p: Exclude<DatePreset, 'custom'>) => {
    const r = computeRange(p, today)
    if (minDate && r.from < minDate) r.from = minDate
    onChange?.(r, p)
  }

  const emitManual = (patch: Partial<DateRange>) => {
    const next: DateRange = { ...range, ...patch }
    onChange?.(next, 'custom')
  }

  return (
    <div
      className={cn(
        'inline-flex items-stretch gap-0.5 rounded-md border border-border-subtle bg-surface-sunk p-0.5',
        className,
      )}
    >
      {PRESETS.map(({ key, label }) => {
        const active = preset === key
        return (
          <button
            key={key}
            type="button"
            onClick={() => selectPreset(key)}
            aria-pressed={active}
            className={cn(
              'cursor-pointer rounded-xs border-none bg-transparent px-2.5 py-1 text-xs font-medium text-secondary',
              'hover:text-primary',
              active && 'bg-surface text-primary shadow-xs',
            )}
          >
            {label}
          </button>
        )
      })}
      <div className="ml-1 inline-flex items-center gap-1.5 text-xs text-secondary">
        <input
          type="date"
          value={range.from}
          min={minDate}
          aria-label="From"
          onChange={(e) => emitManual({ from: e.target.value })}
          className="h-6 w-[var(--date-input-width)] rounded-xs border border-border-default bg-surface px-1.5 text-xs text-primary tabular-nums"
        />
        <span aria-hidden="true">→</span>
        <input
          type="date"
          value={range.to}
          min={minDate}
          aria-label="To"
          onChange={(e) => emitManual({ to: e.target.value })}
          className="h-6 w-[var(--date-input-width)] rounded-xs border border-border-default bg-surface px-1.5 text-xs text-primary tabular-nums"
        />
      </div>
    </div>
  )
}
